import { useEffect, useState } from 'react';
import { api, describeError, type ApiKeyStatus } from './api.js';

/**
 * Add API keys for services Alfred uses. A key goes straight into the server's encrypted store
 * (checked with the service first) and is never shown again, only its last 4 characters.
 */
export function ApiKeysSettings() {
  const [keys, setKeys] = useState<ApiKeyStatus[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .apiKeys()
      .then((r) => {
        setKeys(r.keys);
        setAvailable(r.available);
      })
      .catch(() => {});
  }, []);

  if (!keys) return null;
  return (
    <section>
      <h3>API keys</h3>
      <p className="muted small">
        Keys for services Alfred uses. They’re stored encrypted on your server and never shown
        again, only their last 4 characters.
      </p>
      {!available && <p className="small">Keys need ALFRED_SECRETS_KEY on the server.</p>}
      <ul className="routine-list">
        {keys.map((k) => (
          <KeyRow
            key={k.id}
            k={k}
            onChange={(next, note) => {
              setKeys(next);
              setMessage(note);
            }}
            onError={setMessage}
          />
        ))}
      </ul>
      {message && <p className="small">{message}</p>}
    </section>
  );
}

function KeyRow({
  k,
  onChange,
  onError,
}: {
  k: ApiKeyStatus;
  onChange: (keys: ApiKeyStatus[], note: string) => void;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(k.status === 'missing');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.saveApiKey(k.id, value.trim());
      setValue('');
      setEditing(false);
      onChange(
        res.keys,
        res.checked
          ? `${k.name} key saved and checked.`
          : `${k.name} key saved (couldn’t check it right now; Alfred will tell you if it fails).`,
      );
    } catch (err) {
      onError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      onChange((await api.removeApiKey(k.id)).keys, `${k.name} key removed.`);
    } catch (err) {
      onError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="key-row">
      <div className="budget-top">
        <div>
          <strong>{k.name}</strong>
          <div className="muted small">
            {k.purpose} · get it at {k.where}
          </div>
        </div>
        <span className="small">
          {k.status === 'saved'
            ? `✓ Saved ${k.hint ?? ''}`
            : k.status === 'server'
              ? `✓ On server ${k.hint ?? ''}`
              : 'Not set'}
        </span>
      </div>
      {editing ? (
        <div className="row-gap key-edit">
          <input
            className="reject-reason"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={value}
            placeholder={`Paste your ${k.name} API key`}
            aria-label={`${k.name} API key`}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && value.trim() && void save()}
          />
          <button
            className="btn btn-small btn-primary"
            disabled={busy || !value.trim()}
            onClick={() => void save()}
          >
            {busy ? 'Checking…' : 'Save'}
          </button>
          {k.status !== 'missing' && (
            <button className="btn btn-small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div className="row-gap">
          <button className="link-btn small" onClick={() => setEditing(true)}>
            Replace
          </button>
          {k.status === 'saved' && (
            <button className="link-btn small" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          )}
        </div>
      )}
    </li>
  );
}
